import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState, ACTION_TYPES } from '../hooks/useAppState';
import { getTheme } from '../lib/theme';
import { Shield, Check, Building2 } from 'lucide-react';
import * as api from '../lib/api';
import UpgradeModal from '../components/UpgradeModal';

const ESTADOS_MX = [
  'Aguascalientes','Baja California','Baja California Sur','Campeche','Chiapas','Chihuahua',
  'Ciudad de México','Coahuila','Colima','Durango','Estado de México','Guanajuato','Guerrero',
  'Hidalgo','Jalisco','Michoacán','Morelos','Nayarit','Nuevo León','Oaxaca','Puebla',
  'Querétaro','Quintana Roo','San Luis Potosí','Sinaloa','Sonora','Tabasco','Tamaulipas',
  'Tlaxcala','Veracruz','Yucatán','Zacatecas',
];

export default function RegistroCentroPage() {
  const { state, dispatch } = useAppState();
  const navigate = useNavigate();
  const t = getTheme(state.darkMode);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [limitError, setLimitError] = useState(null);
  // Override global overflow:hidden for scroll
  useEffect(() => {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    document.getElementById('root').style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.getElementById('root').style.overflow = '';
    };
  }, []);

  const [form, setForm] = useState({
    nombreCentro: '', numTrabajadores: '', actividadPrincipal: '',
    calle: '', numExterior: '', numInterior: '', colonia: '', cp: '', municipio: '', estado: '',
    telefono: '', correo: '',
  });

  const set = (key, val) => { setForm(p => ({ ...p, [key]: val })); setErrors(p => ({ ...p, [key]: null })); };

  const handleSubmit = async () => {
    const errs = {};
    if (!form.nombreCentro.trim()) errs.nombreCentro = 'Requerido';
    if (!form.numTrabajadores || parseInt(form.numTrabajadores) < 1) errs.numTrabajadores = 'Requerido';
    if (!form.calle.trim()) errs.calle = 'Requerido';
    if (!form.colonia.trim()) errs.colonia = 'Requerido';
    if (!form.cp.trim()) errs.cp = 'Requerido';
    if (!form.municipio.trim()) errs.municipio = 'Requerido';
    if (!form.estado) errs.estado = 'Requerido';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSaving(true);
    setLimitError(null);
    try {
      const centro = {
        ...form,
        id: 'ct_' + Date.now(),
        numTrabajadores: parseInt(form.numTrabajadores, 10),
        trabajadores: [],
      };
      await api.createCentro(state.empresaId, centro);
      dispatch({ type: ACTION_TYPES.ADD_CENTRO, payload: centro });
      dispatch({ type: ACTION_TYPES.SET_CENTRO_ACTUAL, payload: state.centrosTrabajo.length });
      navigate('/dashboard');
    } catch (err) {
      if (err.code === 'CENTRO_LIMIT') { setLimitError(err.detail); }
      else { alert('Error: ' + err.message); }
    }
    setSaving(false);
  };

  const inputStyle = { width: '100%', padding: '12px 16px', background: t.bgInput || '#fff', border: `1px solid ${t.borderInput || '#e5e7eb'}`, borderRadius: 10, color: t.textStrong || '#1e293b', fontSize: '0.88rem', outline: 'none', boxSizing: 'border-box' };
  const inputErr = (key) => errors[key] ? { borderColor: '#ef4444' } : {};
  const labelStyle = { fontSize: '0.75rem', fontWeight: 600, color: t.textMuted || '#64748b', marginBottom: 6, display: 'block' };
  const errStyle = { fontSize: '0.72rem', color: '#ef4444', marginTop: 3 };
  const sectionTitle = (num, text) => (
    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#6366f1', margin: '20px 0 10px', paddingBottom: 6, borderBottom: `1px solid ${t.border || '#e5e7eb'}` }}>{num}. {text}</div>
  );

  const numTrab = parseInt(form.numTrabajadores) || 0;
  const obligLabel = numTrab <= 0 ? '' : numTrab <= 15 ? 'Política + Guía I' : numTrab <= 50 ? 'Política + Guía I + Guía II' : 'Política + Guía I + Guía III';

  return (
    <div style={{ minHeight: '100vh', background: t.bg || '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', overflowY: 'auto' }}>
      <div style={{ maxWidth: 540, width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <Shield size={20} style={{ color: '#6366f1' }} />
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1' }}>NOM-035-STPS-2018</span>
        </div>
        <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: t.textStrong, margin: '0 0 4px' }}>
          <Building2 size={20} style={{ display: 'inline', marginRight: 6 }} />
          Registrar centro de trabajo
        </h1>
        <p style={{ color: t.textMuted, fontSize: '0.85rem', margin: '0 0 8px' }}>
          {state.empresa?.razonSocial || state.empresa?.nombre || 'Empresa'}
        </p>
        {state.centrosTrabajo.length > 0 && (
          <p style={{ fontSize: '0.78rem', color: t.textMuted, margin: '0 0 16px' }}>
            Ya tienes {state.centrosTrabajo.length} centro{state.centrosTrabajo.length > 1 ? 's' : ''} registrado{state.centrosTrabajo.length > 1 ? 's' : ''}. Las obligaciones NOM-035 aplican por cada centro de trabajo.
          </p>
        )}

        {/* 1. Identificación del centro */}
        {sectionTitle('1', 'Identificación del centro')}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Nombre del centro de trabajo *</label>
            <input style={{ ...inputStyle, ...inputErr('nombreCentro') }} value={form.nombreCentro} onChange={e => set('nombreCentro', e.target.value)} placeholder="Ej. Planta Querétaro" />
            {errors.nombreCentro && <div style={errStyle}>{errors.nombreCentro}</div>}
          </div>
          <div>
            <label style={labelStyle}>Número total de trabajadores en este centro *</label>
            <input style={{ ...inputStyle, ...inputErr('numTrabajadores') }} type="number" min="1" value={form.numTrabajadores} onChange={e => set('numTrabajadores', e.target.value)} placeholder="65" />
            {errors.numTrabajadores && <div style={errStyle}>{errors.numTrabajadores}</div>}
            {obligLabel && (
              <div style={{ marginTop: 6, padding: '8px 12px', background: 'rgba(99,102,241,0.06)', borderRadius: 8, fontSize: '0.75rem', color: '#6366f1', fontWeight: 500 }}>
                Obligaciones para este centro: {obligLabel}
              </div>
            )}
          </div>
          <div>
            <label style={labelStyle}>Actividad principal del centro</label>
            <input style={inputStyle} value={form.actividadPrincipal} onChange={e => set('actividadPrincipal', e.target.value)} placeholder="Ej. Manufactura de productos textiles" />
          </div>
        </div>

        {/* 2. Domicilio */}
        {sectionTitle('2', 'Domicilio del centro de trabajo')}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={labelStyle}>Calle *</label>
            <input style={{ ...inputStyle, ...inputErr('calle') }} value={form.calle} onChange={e => set('calle', e.target.value)} placeholder="Av. Industria" />
            {errors.calle && <div style={errStyle}>{errors.calle}</div>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>No. exterior</label>
              <input style={inputStyle} value={form.numExterior} onChange={e => set('numExterior', e.target.value)} placeholder="456" />
            </div>
            <div>
              <label style={labelStyle}>No. interior</label>
              <input style={inputStyle} value={form.numInterior} onChange={e => set('numInterior', e.target.value)} placeholder="Opcional" />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Colonia *</label>
            <input style={{ ...inputStyle, ...inputErr('colonia') }} value={form.colonia} onChange={e => set('colonia', e.target.value)} placeholder="Parque Industrial" />
            {errors.colonia && <div style={errStyle}>{errors.colonia}</div>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>Código Postal *</label>
              <input style={{ ...inputStyle, ...inputErr('cp') }} value={form.cp} onChange={e => set('cp', e.target.value)} placeholder="76100" />
              {errors.cp && <div style={errStyle}>{errors.cp}</div>}
            </div>
            <div>
              <label style={labelStyle}>Municipio / Alcaldía *</label>
              <input style={{ ...inputStyle, ...inputErr('municipio') }} value={form.municipio} onChange={e => set('municipio', e.target.value)} placeholder="Querétaro" />
              {errors.municipio && <div style={errStyle}>{errors.municipio}</div>}
            </div>
          </div>
          <div>
            <label style={labelStyle}>Estado *</label>
            <select style={{ ...inputStyle, ...inputErr('estado') }} value={form.estado} onChange={e => set('estado', e.target.value)}>
              <option value="">Seleccionar estado...</option>
              {ESTADOS_MX.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            {errors.estado && <div style={errStyle}>{errors.estado}</div>}
          </div>
        </div>

        {/* 3. Contacto */}
        {sectionTitle('3', 'Contacto del centro')}
        <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '-4px 0 10px' }}>Opcional — recomendado para documentación</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={labelStyle}>Teléfono</label>
            <input style={inputStyle} value={form.telefono} onChange={e => set('telefono', e.target.value)} placeholder="442 123 4567" />
          </div>
          <div>
            <label style={labelStyle}>Correo electrónico</label>
            <input style={inputStyle} type="email" value={form.correo} onChange={e => set('correo', e.target.value)} placeholder="planta@empresa.com" />
          </div>
        </div>

        <UpgradeModal error={limitError} onClose={() => setLimitError(null)} />

        {/* Submit */}
        <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${t.border || '#e5e7eb'}`, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={handleSubmit} disabled={saving} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '14px 28px',
            background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10,
            fontSize: '0.92rem', fontWeight: 600, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.7 : 1,
          }}>
            {saving ? 'Guardando...' : 'Registrar centro'} {!saving && <Check size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}
